import { Container, Graphics, Sprite, Text, Texture } from 'pixi.js';
import { gsap } from 'gsap';
import { countryByCode } from '@fbwl/shared';
import { DESIGN } from '../core/runtime.js';
import { FlagCard } from './flag-card.js';

/**
 * The head-to-head stage.
 *
 * Two nations, a contested energy beam between them, and nothing else competing
 * for attention. Everything the viewer needs is inside a single eye movement:
 * flag, name, rating, and the beam that shows who is winning right now.
 *
 * Scores live in the DOM scorebug rather than here - large numbers over a
 * bloomed canvas are the first thing to smear on a low-bitrate stream.
 */
export class DuelStage {
  readonly view = new Container();

  private readonly beam: Graphics;
  private readonly beamGlow: Sprite;
  private readonly vsLabel: Text;
  private readonly homeName: Text;
  private readonly awayName: Text;
  private readonly ratings: Text;

  private homeCard: FlagCard | null = null;
  private awayCard: FlagCard | null = null;
  private balance = 0.5;
  private targetBalance = 0.5;
  private pulse = 0;
  private elapsed = 0;

  /*
   * Geometry is resolved once, in the constructor, from the live design size.
   * The two duellists always face each other left-to-right - vertically stacked
   * flags read as a list, not as a contest - so portrait keeps the same
   * arrangement on narrower cards and lifts it clear of the lower chrome.
   */
  private readonly cardWidth: number;
  private readonly homeX: number;
  private readonly awayX: number;
  private readonly centreY: number;

  constructor() {
    this.cardWidth = DESIGN.portrait ? 300 : 380;
    this.homeX = DESIGN.width * (DESIGN.portrait ? 0.26 : 0.3);
    this.awayX = DESIGN.width * (DESIGN.portrait ? 0.74 : 0.7);
    this.centreY = DESIGN.height * (DESIGN.portrait ? 0.42 : 0.5);

    const CARD_WIDTH = this.cardWidth;
    const HOME_X = this.homeX;
    const AWAY_X = this.awayX;
    const CENTER_Y = this.centreY;

    this.view.eventMode = 'none';
    this.view.visible = false;

    this.beamGlow = new Sprite(createBeamGlowTexture());
    this.beamGlow.anchor.set(0.5);
    this.beamGlow.width = AWAY_X - HOME_X;
    this.beamGlow.height = 200;
    this.beamGlow.position.set((HOME_X + AWAY_X) / 2, CENTER_Y);
    this.beamGlow.blendMode = 'add';
    this.beamGlow.alpha = 0.55;

    this.beam = new Graphics();

    this.vsLabel = label('VS', 54, 0xffffff, 0.24);
    this.vsLabel.position.set(DESIGN.width / 2, CENTER_Y - CARD_WIDTH * 0.35);

    this.homeName = label('', 30, 0xeaf2ff, 0.1);
    this.homeName.position.set(HOME_X, CENTER_Y + CARD_WIDTH * 0.5);

    this.awayName = label('', 30, 0xeaf2ff, 0.1);
    this.awayName.position.set(AWAY_X, CENTER_Y + CARD_WIDTH * 0.5);

    this.ratings = label('', 20, 0x9bb0cc, 0.16);
    this.ratings.position.set(DESIGN.width / 2, CENTER_Y + CARD_WIDTH * 0.39);

    this.view.addChild(
      this.beamGlow,
      this.beam,
      this.vsLabel,
      this.homeName,
      this.awayName,
      this.ratings,
    );
  }

  get isActive(): boolean {
    return this.view.visible;
  }

  /** Where a side's card sits, so particles and the camera can aim at it. */
  anchorFor(side: 'home' | 'away'): { x: number; y: number } {
    return { x: side === 'home' ? this.homeX : this.awayX, y: this.centreY };
  }

  get centre(): { x: number; y: number } {
    return { x: DESIGN.width / 2, y: this.centreY };
  }

  setup(homeCode: string, awayCode: string, homeRating: number, awayRating: number): void {
    this.teardown();

    this.homeCard = new FlagCard(homeCode, { width: this.cardWidth, showCode: true, glow: true });
    this.awayCard = new FlagCard(awayCode, { width: this.cardWidth, showCode: true, glow: true });

    this.homeCard.position.set(this.homeX - 620, this.centreY);
    this.awayCard.position.set(this.awayX + 620, this.centreY);
    this.view.addChildAt(this.homeCard, 0);
    this.view.addChildAt(this.awayCard, 0);

    this.homeName.text = (countryByCode(homeCode)?.name ?? homeCode).toUpperCase();
    this.awayName.text = (countryByCode(awayCode)?.name ?? awayCode).toUpperCase();
    this.ratings.text = `${Math.round(homeRating)}  RATING  ${Math.round(awayRating)}`;

    this.balance = 0.5;
    this.targetBalance = 0.5;
    this.view.visible = true;
    this.view.alpha = 0;

    gsap.to(this.view, { alpha: 1, duration: 0.5, ease: 'power2.out' });
    gsap.to(this.homeCard, { x: this.homeX, duration: 1.05, ease: 'back.out(1.3)' });
    gsap.to(this.awayCard, { x: this.awayX, duration: 1.05, ease: 'back.out(1.3)' });
    gsap.fromTo(
      this.vsLabel.scale,
      { x: 3, y: 3 },
      { x: 1, y: 1, duration: 0.75, ease: 'back.out(2)' },
    );
    gsap.fromTo(this.vsLabel, { alpha: 0 }, { alpha: 1, duration: 0.6 });
  }

  /** 0 means the away side dominates, 1 means the home side does. */
  setBalance(value: number): void {
    this.targetBalance = Math.min(0.94, Math.max(0.06, value));
  }

  /** Visual kick for a scoring strike. */
  strike(side: 'home' | 'away', critical: boolean): void {
    const card = side === 'home' ? this.homeCard : this.awayCard;
    const other = side === 'home' ? this.awayCard : this.homeCard;

    card?.react(side === 'home' ? -1 : 1);
    other?.react(side === 'home' ? 1 : -1);

    this.pulse = critical ? 1 : 0.55;

    if (critical && card) {
      gsap.fromTo(
        card.scale,
        { x: card.scale.x * 1.18, y: card.scale.y * 1.18 },
        { x: 1, y: 1, duration: 0.8, ease: 'elastic.out(1, 0.45)' },
      );
    }
  }

  /** Winner surges forward and glows; loser dims and falls away. */
  finish(winner: 'home' | 'away'): void {
    const winnerCard = winner === 'home' ? this.homeCard : this.awayCard;
    const loserCard = winner === 'home' ? this.awayCard : this.homeCard;

    this.setBalance(winner === 'home' ? 0.94 : 0.06);

    if (winnerCard) {
      winnerCard.setHighlight(true, 0xffc94d);
      gsap.to(winnerCard, {
        x: DESIGN.width / 2,
        duration: 1.1,
        ease: 'power3.inOut',
        delay: 0.35,
      });
      gsap.to(winnerCard.scale, {
        x: 1.24,
        y: 1.24,
        duration: 1.1,
        ease: 'power3.inOut',
        delay: 0.35,
      });
    }

    if (loserCard) {
      gsap.to(loserCard, {
        alpha: 0,
        y: this.centreY + 200,
        rotation: winner === 'home' ? 0.4 : -0.4,
        duration: 0.95,
        ease: 'power2.in',
        delay: 0.2,
      });
    }

    gsap.to([this.homeName, this.awayName, this.ratings, this.vsLabel], {
      alpha: 0,
      duration: 0.5,
      delay: 0.3,
    });
  }

  teardown(): void {
    gsap.killTweensOf([this.homeCard, this.awayCard].filter(Boolean));
    this.homeCard?.destroy({ children: true });
    this.awayCard?.destroy({ children: true });
    this.homeCard = null;
    this.awayCard = null;
    this.view.visible = false;
    this.homeName.alpha = 1;
    this.awayName.alpha = 1;
    this.ratings.alpha = 1;
    this.vsLabel.alpha = 1;
  }

  update(deltaSeconds: number): void {
    if (!this.view.visible) return;

    this.elapsed += deltaSeconds;
    this.balance += (this.targetBalance - this.balance) * Math.min(1, deltaSeconds * 3.2);
    this.pulse = Math.max(0, this.pulse - deltaSeconds * 2.2);

    const left = this.homeX + this.cardWidth * 0.5 + 24;
    const right = this.awayX - this.cardWidth * 0.5 - 24;
    const meeting = left + (right - left) * this.balance;
    const wobble = Math.sin(this.elapsed * 6) * 4 * (0.4 + this.pulse);
    const thickness = 10 + this.pulse * 26 + Math.sin(this.elapsed * 9) * 2;

    const homeColor = this.homeCard?.accent ?? 0x38bdf8;
    const awayColor = this.awayCard?.accent ?? 0xffc94d;

    this.beam.clear();
    this.beam
      .moveTo(left, this.centreY)
      .lineTo(meeting, this.centreY + wobble)
      .stroke({ width: thickness, color: homeColor, alpha: 0.9, cap: 'round' });
    this.beam
      .moveTo(meeting, this.centreY + wobble)
      .lineTo(right, this.centreY)
      .stroke({ width: thickness, color: awayColor, alpha: 0.9, cap: 'round' });
    this.beam
      .circle(meeting, this.centreY + wobble, 16 + this.pulse * 30)
      .fill({ color: 0xffffff, alpha: 0.75 });

    this.beamGlow.x = meeting;
    this.beamGlow.alpha = 0.4 + this.pulse * 0.5;
    this.beamGlow.height = 150 + this.pulse * 180;
    this.beamGlow.tint = this.balance > 0.5 ? homeColor : awayColor;
  }
}

function label(text: string, size: number, color: number, spacing: number): Text {
  const node = new Text({
    text,
    style: {
      fontFamily: 'Bahnschrift, "Segoe UI", system-ui, sans-serif',
      fontSize: size,
      fontWeight: '700',
      fill: color,
      letterSpacing: size * spacing,
      align: 'center',
      dropShadow: { color: 0x000000, alpha: 0.7, blur: 8, distance: 0, angle: 0 },
    },
  });
  node.anchor.set(0.5);
  return node;
}

function createBeamGlowTexture(): Texture {
  const canvas = document.createElement('canvas');
  canvas.width = 256;
  canvas.height = 128;
  const ctx = canvas.getContext('2d');
  if (!ctx) return Texture.WHITE;

  const gradient = ctx.createRadialGradient(128, 64, 0, 128, 64, 120);
  gradient.addColorStop(0, 'rgba(255,255,255,0.9)');
  gradient.addColorStop(0.35, 'rgba(255,255,255,0.35)');
  gradient.addColorStop(1, 'rgba(255,255,255,0)');
  ctx.fillStyle = gradient;
  ctx.fillRect(0, 0, 256, 128);

  return Texture.from(canvas);
}
